import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  BatteryFull,
  Bell,
  Bookmark,
  BriefcaseBusiness,
  Camera,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Globe,
  FileText,
  Heart,
  House,
  Inbox,
  MapPin,
  Menu,
  MessageCircle,
  MessageCircleMore,
  MoreHorizontal,
  Music2,
  Plus,
  Repeat2,
  Search,
  Send,
  Share2,
  Signal,
  SquarePlus,
  Store,
  ThumbsUp,
  TvMinimalPlay,
  User,
  Users,
  Wifi,
  X,
} from "lucide-react";

import { UnifiedModal } from "../../../components/UnifiedModal";
import { getFormatRule, isDocumentMedia, isVideoMedia, platformLabel, postTextSx } from "./platformMeta";
import { VideoPoster } from "./VideoMedia";
import { NO_CALL_TO_ACTION, callToActionMeta, callToActionOf } from "./callToAction";
import { networkTextHtml } from "./workflowUtils";

// Crop each network applies to the feed image (the upload specs of ContentPreviewCard).
// Instagram and Facebook: only when the image size is unknown (see feedRatio).
// TikTok photo mode fills the screen and letterboxes instead.
const IMAGE_RATIO = {
  instagram: "4 / 5",
  facebook: "1.91 / 1",
  linkedin: "1.91 / 1",
  tiktok: null,
  google_business_profile: "4 / 3",
};

/** Instagram and Facebook keep the first image's own shape within the post limits; the rest of a carousel take it too. */
const feedRatio = (platform, images) => {
  const { width, height } = images[0] || {};
  if (!["instagram", "facebook"].includes(platform) || !width || !height) return IMAGE_RATIO[platform];
  const { minImageAspect, maxImageAspect } = getFormatRule(platform, "post");
  return String(Math.min(Math.max(width / height, minImageAspect || 0), maxImageAspect || Infinity));
};

// Lines each network shows before "more".
const TEXT_LINES = { instagram: 2, facebook: 5, linkedin: 3, tiktok: 2, google_business_profile: 6 };

export const hasPostPreview = (platform) => platform in IMAGE_RATIO;

const hideScrollbarSx = { scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } };

function Avatar({ name, size = 36, round = true, ring = false }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: round ? "50%" : "6px",
        background: "linear-gradient(135deg, #8153ff, #E1306C)",
        color: "#fff",
        fontWeight: 700,
        fontSize: size * 0.42,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(ring ? { boxShadow: "0 0 0 2px #fff, 0 0 0 4px #E1306C" } : {}),
      }}
    >
      {(name || "?").trim().charAt(0).toUpperCase()}
    </Box>
  );
}

function PostText({ text, platform, lines, prefix = null, sx = {}, expandedSx = {} }) {
  const [open, setOpen] = useState(false);
  const value = text || "";
  const long = value.split("\n").length > lines || value.length > lines * 45;
  return (
    <Box sx={{ fontSize: "0.8125rem", lineHeight: 1.4, wordBreak: "break-word", ...sx }}>
      <Box
        sx={{
          ...(open || !long
            ? {}
            : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }),
          ...(open ? expandedSx : {}),
          ...postTextSx(platform),
        }}
      >
        {prefix}
        <span dangerouslySetInnerHTML={{ __html: networkTextHtml(platform, value) }} />
      </Box>
      {long && !open && (
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{ p: 0, border: "none", background: "none", color: "inherit", opacity: 0.6, cursor: "pointer", font: "inherit" }}
        >
          … más
        </Box>
      )}
    </Box>
  );
}

const arrowSx = (side, inset) => ({
  position: "absolute",
  top: "50%",
  [side]: inset,
  transform: "translateY(-50%)",
  zIndex: 3,
  width: 26,
  height: 26,
  backgroundColor: "rgba(255,255,255,0.9)",
  color: "#111827",
  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  "&:hover": { backgroundColor: "#fff" },
});

/** Swipeable images: cropped to `ratio`, or filling the parent letterboxed when it is null. */
function Media({ images, ratio, dots = "below", rightArrowInset = 6, autoplay = false }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const fill = !ratio;

  useEffect(() => {
    setIndex(0);
    trackRef.current?.scrollTo({ left: 0 });
  }, [images]);

  if (!images.length) return null;

  const goTo = (next) => {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (track?.clientWidth) setIndex(Math.round(track.scrollLeft / track.clientWidth));
  };
  const many = images.length > 1;
  const overlay = dots === "overlay";

  const dotRow = (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
      {images.map((image, i) => {
        const on = i === index;
        return (
          <Box
            key={image.id ?? i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: overlay ? (on ? "#fff" : "rgba(255,255,255,0.45)") : on ? "#0095F6" : "#D1D5DB",
            }}
          />
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ position: "relative", ...(fill ? { height: "100%" } : {}) }}>
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          height: fill ? "100%" : "auto",
          ...hideScrollbarSx,
        }}
      >
        {images.map((image, i) => (
          <Box
            key={image.id ?? i}
            sx={{
              flex: "0 0 100%",
              scrollSnapAlign: "start",
              ...(fill ? { height: "100%" } : { aspectRatio: ratio }),
              backgroundColor: fill ? "#000" : "#F3F4F6",
            }}
          >
            {isDocumentMedia(image) ? (
              // LinkedIn PDF: the title bar on top of the first page, as the feed shows it.
              <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 160, backgroundColor: "#fff" }}>
                <Box sx={{ px: 1.25, py: 0.75, backgroundColor: "rgba(0,0,0,0.75)", color: "#fff", fontSize: "0.6875rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {image.title || "Título del documento"}
                </Box>
                <Box sx={{ position: "absolute", inset: "28px 0 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.75, color: "#B91C1C" }}>
                  <FileText size={34} />
                  <Box sx={{ fontSize: "0.6875rem", color: "#6B7280" }}>Documento PDF</Box>
                </Box>
              </Box>
            ) : isVideoMedia(image) ? (
              image.video_url ? (
                <Box
                  component="video"
                  src={image.video_url}
                  poster={image.image || undefined}
                  // Reels e historias: el vídeo corre solo, sin controles encima de la interfaz.
                  {...(autoplay ? { autoPlay: true, loop: true } : { controls: true })}
                  muted
                  playsInline
                  preload="metadata"
                  sx={{ width: "100%", height: "100%", objectFit: fill ? "contain" : "cover", display: "block", backgroundColor: "#000" }}
                />
              ) : (
                <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 120 }}>
                  <VideoPoster src={image.image} playSize={36} />
                </Box>
              )
            ) : (
              <Box
                component="img"
                src={image.image}
                alt=""
                draggable={false}
                sx={{ width: "100%", height: "100%", objectFit: fill ? "contain" : "cover", display: "block" }}
              />
            )}
          </Box>
        ))}
      </Box>
      {many && (
        <>
          {index > 0 && (
            <IconButton aria-label="Imagen anterior" size="small" onClick={() => goTo(index - 1)} sx={arrowSx("left", 6)}>
              <ChevronLeft size={15} />
            </IconButton>
          )}
          {index < images.length - 1 && (
            <IconButton
              aria-label="Imagen siguiente"
              size="small"
              onClick={() => goTo(index + 1)}
              sx={arrowSx("right", rightArrowInset)}
            >
              <ChevronRight size={15} />
            </IconButton>
          )}
          {overlay ? (
            <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 8, zIndex: 2 }}>{dotRow}</Box>
          ) : (
            <>
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  px: 0.875,
                  borderRadius: "999px",
                  backgroundColor: "rgba(17,24,39,0.7)",
                  color: "#fff",
                  fontSize: "0.6875rem",
                  zIndex: 2,
                }}
              >
                {index + 1}/{images.length}
              </Box>
              <Box sx={{ py: 0.75 }}>{dotRow}</Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

function StatusBar({ dark }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        height: 34,
        flexShrink: 0,
        color: dark ? "#fff" : "#111827",
        backgroundColor: dark ? "#000" : "#fff",
        fontSize: "0.75rem",
        fontWeight: 700,
      }}
    >
      <span>9:41</span>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 7,
          transform: "translateX(-50%)",
          width: 88,
          height: 22,
          borderRadius: "999px",
          backgroundColor: "#000",
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Signal size={13} />
        <Wifi size={13} />
        <BatteryFull size={16} />
      </Box>
    </Box>
  );
}

function Phone({ dark = false, children }) {
  const background = dark ? "#000" : "#fff";
  return (
    <Box
      sx={{
        width: 320,
        height: 660,
        maxWidth: "100%",
        mx: "auto",
        p: "10px",
        borderRadius: "46px",
        backgroundColor: "#111",
        boxShadow: "0 0 0 2px #2A2A2A, 0 18px 40px rgba(15, 23, 42, 0.25)",
      }}
    >
      <Box
        sx={{
          height: "100%",
          borderRadius: "36px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: background,
        }}
      >
        <StatusBar dark={dark} />
        {children}
        <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", py: 0.75, backgroundColor: background }}>
          <Box sx={{ width: 110, height: 4, borderRadius: "999px", backgroundColor: dark ? "#fff" : "#111" }} />
        </Box>
      </Box>
    </Box>
  );
}

function Screen({ children, sx = {} }) {
  return <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...hideScrollbarSx, ...sx }}>{children}</Box>;
}

function BottomNav({ items, dark = false }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        pt: 0.75,
        borderTop: dark ? "none" : "1px solid #E5E7EB",
        backgroundColor: dark ? "#000" : "#fff",
        color: dark ? "#fff" : "#111827",
      }}
    >
      {items.map(({ icon: Icon, label, node, active }, i) => (
        <Box
          key={label || i}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.25,
            fontSize: "0.5625rem",
            opacity: active || node ? 1 : 0.65,
          }}
        >
          {node || <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />}
          {label && <span>{label}</span>}
        </Box>
      ))}
    </Box>
  );
}

function InstagramPhone({ name, text, images }) {
  return (
    <Phone>
      <Box sx={{ display: "flex", alignItems: "center", px: 1.75, py: 0.75, flexShrink: 0 }}>
        <Typography sx={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontWeight: 700, fontSize: "1.35rem", flex: 1 }}>
          Instagram
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Heart size={21} />
          <Send size={21} />
        </Box>
      </Box>
      <Screen>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1 }}>
          <Avatar name={name} size={30} ring />
          <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", flex: 1, ml: 0.5 }}>{name}</Typography>
          <MoreHorizontal size={18} />
        </Box>
        <Media images={images} ratio={feedRatio("instagram", images)} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, px: 1.5, pt: images.length > 1 ? 0 : 1 }}>
          <Heart size={21} />
          <MessageCircle size={21} />
          <Send size={21} />
          <Box sx={{ flex: 1 }} />
          <Bookmark size={21} />
        </Box>
        <PostText
          text={text}
          platform="instagram"
          lines={TEXT_LINES.instagram}
          prefix={<strong style={{ marginRight: 6 }}>{name}</strong>}
          sx={{ px: 1.5, py: 1 }}
        />
        <Typography sx={{ px: 1.5, pb: 2, fontSize: "0.6875rem", color: "#6B7280" }}>Ahora</Typography>
      </Screen>
      <BottomNav
        items={[
          { icon: House, active: true },
          { icon: Search },
          { icon: SquarePlus },
          { icon: Clapperboard },
          { node: <Avatar name={name} size={22} /> },
        ]}
      />
    </Phone>
  );
}

function FeedCard({ platform, name, subtitle, text, images, actions, avatarRound = true }) {
  return (
    <Box sx={{ backgroundColor: "#fff", color: "#1F2937", pb: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, pt: 1.25 }}>
        <Avatar name={name} size={36} round={avatarRound} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>{name}</Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: "#6B7280", display: "flex", alignItems: "center", gap: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <MoreHorizontal size={18} color="#6B7280" />
      </Box>
      <PostText text={text} platform={platform} lines={TEXT_LINES[platform]} sx={{ px: 1.5, py: 1 }} />
      <Media images={images} ratio={feedRatio(platform, images)} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          borderTop: "1px solid #E5E7EB",
          mx: 1,
          mt: images.length > 1 ? 0 : 0.75,
          py: 0.875,
          color: "#4B5563",
          fontSize: "0.6875rem",
          fontWeight: 600,
        }}
      >
        {actions.map(({ icon: Icon, label, href, hint }) => {
          const action = (
            <Box
              key={label}
              {...(href ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" } : {})}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, color: "inherit", textDecoration: "none" }}
            >
              <Icon size={16} /> {label}
            </Box>
          );
          return hint ? (
            <Tooltip key={label} title={hint} arrow>
              {action}
            </Tooltip>
          ) : (
            action
          );
        })}
      </Box>
    </Box>
  );
}

const publicNow = (
  <>
    Ahora · <Globe size={10} />
  </>
);

function LinkedInPhone({ name, text, images }) {
  return (
    <Phone>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.75, flexShrink: 0 }}>
        <Avatar name={name} size={26} />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: "4px",
            backgroundColor: "#EEF3F8",
            color: "#6B7280",
            fontSize: "0.75rem",
          }}
        >
          <Search size={14} /> Buscar
        </Box>
        <MessageCircleMore size={21} color="#4B5563" />
      </Box>
      <Screen sx={{ backgroundColor: "#F4F2EE", pt: 0.75 }}>
        <FeedCard
          platform="linkedin"
          name={name}
          subtitle={publicNow}
          text={text}
          images={images}
          avatarRound={false}
          actions={[
            { icon: ThumbsUp, label: "Recomendar" },
            { icon: MessageCircle, label: "Comentar" },
            { icon: Repeat2, label: "Compartir" },
            { icon: Send, label: "Enviar" },
          ]}
        />
      </Screen>
      <BottomNav
        items={[
          { icon: House, label: "Inicio", active: true },
          { icon: Users, label: "Mi red" },
          { icon: SquarePlus, label: "Publicar" },
          { icon: Bell, label: "Notificaciones" },
          { icon: BriefcaseBusiness, label: "Empleos" },
        ]}
      />
    </Phone>
  );
}

const facebookCircleSx = {
  width: 30,
  height: 30,
  borderRadius: "50%",
  backgroundColor: "#E4E6EB",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function FacebookPhone({ name, text, images }) {
  return (
    <Phone>
      <Box sx={{ display: "flex", alignItems: "center", px: 1.5, pt: 0.5, flexShrink: 0 }}>
        <Typography sx={{ color: "#0866FF", fontWeight: 800, fontSize: "1.45rem", letterSpacing: "-0.03em", flex: 1 }}>
          facebook
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Box sx={facebookCircleSx}><Plus size={17} /></Box>
          <Box sx={facebookCircleSx}><Search size={16} /></Box>
          <Box sx={facebookCircleSx}><MessageCircle size={16} /></Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          py: 0.75,
          borderBottom: "1px solid #E5E7EB",
          color: "#65676B",
          flexShrink: 0,
        }}
      >
        <House size={20} color="#0866FF" />
        <Users size={20} />
        <TvMinimalPlay size={20} />
        <Store size={20} />
        <Bell size={20} />
        <Menu size={20} />
      </Box>
      <Screen sx={{ backgroundColor: "#F0F2F5", pt: 0.75 }}>
        <FeedCard
          platform="facebook"
          name={name}
          subtitle={publicNow}
          text={text}
          images={images}
          actions={[
            { icon: ThumbsUp, label: "Me gusta" },
            { icon: MessageCircle, label: "Comentar" },
            { icon: Share2, label: "Compartir" },
          ]}
        />
      </Screen>
    </Phone>
  );
}

function GoogleBusinessPhone({ name, text, images, callToAction }) {
  const button = callToActionMeta(callToAction?.type);
  return (
    <Phone>
      <Box sx={{ px: 1.5, py: 0.75, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            fontSize: "0.75rem",
            color: "#3C4043",
          }}
        >
          <MapPin size={15} color="#EA4335" />
          <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Box>
          <Avatar name={name} size={22} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 2, px: 2, borderBottom: "1px solid #E5E7EB", fontSize: "0.75rem", color: "#5F6368", flexShrink: 0 }}>
        <Box sx={{ py: 0.75 }}>Resumen</Box>
        <Box sx={{ py: 0.75, color: "#1A73E8", fontWeight: 700, borderBottom: "2px solid #1A73E8" }}>Novedades</Box>
        <Box sx={{ py: 0.75 }}>Reseñas</Box>
        <Box sx={{ py: 0.75 }}>Fotos</Box>
      </Box>
      <Screen sx={{ pt: 0.75 }}>
        <FeedCard
          platform="google_business_profile"
          name={name}
          subtitle="Ahora"
          text={text}
          images={images}
          actions={[
            { icon: Share2, label: "Compartir" },
            ...(button
              ? [
                  {
                    icon: button.icon,
                    label: button.label,
                    href: button.needsUrl ? callToAction.url || undefined : undefined,
                    hint: button.needsUrl ? callToAction.url || "Sin enlace" : "Llama al teléfono de la ficha",
                  },
                ]
              : []),
          ]}
        />
      </Screen>
    </Phone>
  );
}

function TikTokSideAction({ icon: Icon, count, filled = true }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, fontSize: "0.625rem", fontWeight: 700 }}>
      <Icon size={27} fill={filled ? "#fff" : "none"} strokeWidth={filled ? 0 : 2} />
      {count}
    </Box>
  );
}

function TikTokPhone({ name, text, images }) {
  const handle = (name || "cuenta").toLowerCase().replace(/\s+/g, "");
  const many = images.length > 1;
  return (
    <Phone dark>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, backgroundColor: "#000", color: "#fff" }}>
        {images.length ? (
          // The right arrow sits left of the action column, which would otherwise cover it.
          // Video plays on its own, as in the app: native controls would sit under the caption.
          <Media images={images} ratio={IMAGE_RATIO.tiktok} dots="overlay" rightArrowInset={58} autoplay />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: "0.8125rem" }}>
            Sin imágenes
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            fontSize: "0.875rem",
            fontWeight: 700,
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box sx={{ opacity: 0.6 }}>Siguiendo</Box>
          <Box sx={{ pb: 0.5, borderBottom: "2px solid #fff" }}>Para ti</Box>
          <Box sx={{ position: "absolute", right: 14, display: "flex" }}>
            <Search size={20} />
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 70,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.75,
            zIndex: 2,
          }}
        >
          <Box sx={{ position: "relative", mb: 0.75 }}>
            <Avatar name={name} size={40} />
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                bottom: -8,
                transform: "translateX(-50%)",
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#FE2C55",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Plus size={12} strokeWidth={3} />
            </Box>
          </Box>
          <TikTokSideAction icon={Heart} count="0" />
          <TikTokSideAction icon={MessageCircle} count="0" />
          <TikTokSideAction icon={Bookmark} count="0" />
          <TikTokSideAction icon={Share2} count="0" filled={false} />
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "radial-gradient(circle, #555 0 30%, #222 31% 100%)",
              border: "6px solid #2A2A2A",
              animation: "mkTikTokDisc 4s linear infinite",
              "@keyframes mkTikTokDisc": { to: { transform: "rotate(360deg)" } },
            }}
          />
        </Box>

        {/* Dark band under the caption so it reads over any video. */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "45%",
            background: "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 56,
            bottom: many ? 22 : 8,
            px: 1.5,
            zIndex: 2,
            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", mb: 0.5 }}>{handle}</Typography>
          {/* Expanded ("… más"): scrolls inside a dark band, as in the app, instead of covering the video. */}
          <PostText
            text={text}
            platform="tiktok"
            lines={TEXT_LINES.tiktok}
            expandedSx={{ maxHeight: 150, overflowY: "auto", ...hideScrollbarSx }}
            sx={{ "& .mk-hashtag": { color: "#fff" } }}
          />
          <Typography sx={{ fontSize: "0.75rem", mt: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Music2 size={12} /> Sonido original - {handle}
          </Typography>
        </Box>
      </Box>
      <BottomNav
        dark
        items={[
          { icon: House, label: "Inicio", active: true },
          { icon: Users, label: "Amigos" },
          {
            node: (
              <Box
                sx={{
                  width: 40,
                  height: 26,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "-3px 0 0 #25F4EE, 3px 0 0 #FE2C55",
                }}
              >
                <Plus size={18} strokeWidth={3} />
              </Box>
            ),
          },
          { icon: Inbox, label: "Bandeja" },
          { icon: User, label: "Perfil" },
        ]}
      />
    </Phone>
  );
}

const overlayShadowSx = { textShadow: "0 1px 2px rgba(0,0,0,0.5)" };

function FullScreenMedia({ images, rightArrowInset = 6 }) {
  return images.length ? (
    <Media images={images} ratio={null} dots="overlay" rightArrowInset={rightArrowInset} autoplay />
  ) : (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: "0.8125rem" }}>
      Sin vídeo
    </Box>
  );
}

function ReelSideAction({ icon: Icon, count }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, fontSize: "0.6875rem", fontWeight: 600 }}>
      <Icon size={24} />
      {count}
    </Box>
  );
}

/** Reel de Instagram o Facebook: vídeo a pantalla completa, acciones a la derecha y texto abajo. */
function ReelPhone({ platform, name, text, images }) {
  const isFacebook = platform === "facebook";
  return (
    <Phone dark>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, backgroundColor: "#000", color: "#fff" }}>
        <FullScreenMedia images={images} rightArrowInset={52} />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 18%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.55) 100%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 14,
            right: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 2,
            pointerEvents: "none",
            ...overlayShadowSx,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>Reels</Typography>
          {isFacebook ? <Search size={21} /> : <Camera size={22} />}
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.75,
            zIndex: 2,
            ...overlayShadowSx,
          }}
        >
          <ReelSideAction icon={isFacebook ? ThumbsUp : Heart} count="0" />
          <ReelSideAction icon={MessageCircle} count="0" />
          <ReelSideAction icon={isFacebook ? Share2 : Send} count="0" />
          <MoreHorizontal size={20} />
          <Box sx={{ width: 26, height: 26, borderRadius: "6px", border: "2px solid #fff", overflow: "hidden" }}>
            <Avatar name={name} size={22} round={false} />
          </Box>
        </Box>

        <Box sx={{ position: "absolute", left: 0, right: 52, bottom: 14, px: 1.5, zIndex: 2, ...overlayShadowSx }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
            <Avatar name={name} size={28} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>{name}</Typography>
            <Box
              sx={{
                px: 1,
                py: 0.125,
                border: "1px solid rgba(255,255,255,0.8)",
                borderRadius: "8px",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              Seguir
            </Box>
          </Box>
          <PostText text={text} platform={platform} lines={1} sx={{ "& .mk-hashtag": { color: "#fff" } }} />
          <Typography sx={{ fontSize: "0.75rem", mt: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Music2 size={12} /> {name} · Audio original
          </Typography>
        </Box>
      </Box>
      <BottomNav
        dark
        items={
          isFacebook
            ? [
              { icon: House },
              { icon: TvMinimalPlay, active: true },
              { icon: Users },
              { icon: Store },
              { icon: Bell },
              { icon: Menu },
            ]
            : [
              { icon: House },
              { icon: Search },
              { icon: SquarePlus },
              { icon: Clapperboard, active: true },
              { node: <Avatar name={name} size={22} /> },
            ]
        }
      />
    </Phone>
  );
}

/** Historia de Instagram o Facebook: sin texto, barras de progreso y respuesta abajo. */
function StoryPhone({ platform, name, images }) {
  const isFacebook = platform === "facebook";
  const segments = Math.max(images.length, 1);
  return (
    <Phone dark>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, backgroundColor: "#000", color: "#fff", borderRadius: "12px", overflow: "hidden" }}>
        <FullScreenMedia images={images} />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 20%)",
            pointerEvents: "none",
          }}
        />
        <Box sx={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 2, pointerEvents: "none", ...overlayShadowSx }}>
          <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
            {Array.from({ length: segments }, (_, i) => (
              <Box
                key={i}
                sx={{ flex: 1, height: 2.5, borderRadius: 999, backgroundColor: i === 0 ? "#fff" : "rgba(255,255,255,0.4)" }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar name={name} size={30} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>{name}</Typography>
            <Typography sx={{ fontSize: "0.75rem", opacity: 0.75, flex: 1 }}>Ahora</Typography>
            <MoreHorizontal size={18} />
            <X size={22} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1.5, px: 1.5, pt: 1, pb: 0.5, backgroundColor: "#000", color: "#fff" }}>
        <Box
          sx={{
            flex: 1,
            px: 1.75,
            py: 0.875,
            border: "1px solid rgba(255,255,255,0.55)",
            borderRadius: "999px",
            fontSize: "0.8125rem",
            color: "rgba(255,255,255,0.85)",
          }}
        >
          {isFacebook ? "Responder…" : "Enviar mensaje"}
        </Box>
        {isFacebook ? <ThumbsUp size={22} /> : <Heart size={22} />}
        <Send size={22} />
      </Box>
    </Phone>
  );
}

// Formatos con su propia pantalla; el resto usa la del feed de la red.
const FORMAT_PHONES = {
  reel: ReelPhone,
  story: StoryPhone,
};

const PHONES = {
  instagram: InstagramPhone,
  linkedin: LinkedInPhone,
  facebook: FacebookPhone,
  tiktok: TikTokPhone,
  google_business_profile: GoogleBusinessPhone,
};

/** The post inside the network's app, with the approximation note. `images`: [{ image: url }]. */
export function PostPhonePreview({
  platform,
  text,
  images = [],
  accountName,
  format = "post",
  callToAction = NO_CALL_TO_ACTION,
}) {
  const PhoneView = (["instagram", "facebook"].includes(platform) && FORMAT_PHONES[format]) || PHONES[platform];
  // Stories carry no text in the network.
  const shownText = getFormatRule(platform, format).noText ? "" : text || "";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {PhoneView && <PhoneView platform={platform} name={accountName || "Tu marca"} text={shownText} images={images} callToAction={callToAction} />}
      <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", textAlign: "center" }}>
        Aproximación: la red puede variar ligeramente el recorte y el corte del texto.
      </Typography>
    </Box>
  );
}

/** How the adaptation will look once published, inside the network's app. */
export function PostPreviewModal({ open, onClose, adaptation, accountName }) {
  const platform = adaptation?.platform;
  return (
    <UnifiedModal open={open} onClose={onClose} title={`Vista previa · ${platformLabel(platform)}`} maxWidth="xs">
      <Box sx={{ py: 1 }}>
        {adaptation && (
          <PostPhonePreview
            platform={platform}
            text={adaptation.text}
            images={adaptation.images || []}
            accountName={accountName}
            format={adaptation.format}
            callToAction={callToActionOf(adaptation)}
          />
        )}
      </Box>
    </UnifiedModal>
  );
}
